import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchMarketingAssetsData } from "../../../store/marketingAssetsSlice";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Checkbox,
  Breadcrumbs,
  Link,
  Paper,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Folder as FolderIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
} from "@mui/icons-material";

const MarketingAssetPickerDialog = ({ open, onClose, onSelect, contentType }) => {
  const dispatch = useDispatch();
  const assets = useSelector((state) => state.marketingAssets.assets);
  const folders = useSelector((state) => state.marketingAssets.folders);
  const status = useSelector((state) => state.marketingAssets.status);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentFolderPath, setCurrentFolderPath] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  // Fetch assets if not yet loaded; reset selection on close
  useEffect(() => {
    if (open && status === "idle") {
      dispatch(fetchMarketingAssetsData());
    }
    if (!open) {
      setSelectedIds([]);
      setSearchQuery("");
      setCurrentFolderPath("");
    }
  }, [open, status, dispatch]);

  // Assets visible in the current folder + matching search
  const filteredAssets = useMemo(() => {
    const allowedTypes = contentType === "Image" ? ["image"] : ["video"];
    return assets.filter((asset) => {
      if (!allowedTypes.includes(asset.type)) return false;
      const normalizedFolder = asset.folderPath
        ? asset.folderPath.endsWith("/")
          ? asset.folderPath
          : `${asset.folderPath}/`
        : "";
      if (normalizedFolder !== currentFolderPath) return false;
      if (searchQuery && !asset.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [assets, contentType, currentFolderPath, searchQuery]);

  // Sub-folders at current level
  const subFolders = useMemo(() => {
    return folders.filter((f) => f.parentFolderPath === currentFolderPath);
  }, [folders, currentFolderPath]);

  const toggleSelect = (assetId) => {
    setSelectedIds((prev) =>
      prev.includes(assetId) ? prev.filter((id) => id !== assetId) : [...prev, assetId]
    );
  };

  const handleConfirm = () => {
    const selected = assets.filter((a) => selectedIds.includes(a.id));
    onSelect(selected);
    onClose();
  };

  // Breadcrumb navigation
  const breadcrumbParts = currentFolderPath
    ? currentFolderPath.replace(/\/$/, "").split("/").filter(Boolean)
    : [];

  const navigateToFolder = (index) => {
    if (index < 0) {
      setCurrentFolderPath("");
    } else {
      setCurrentFolderPath(breadcrumbParts.slice(0, index + 1).join("/") + "/");
    }
  };

  const isLoading = status === "loading";
  const isEmpty = !isLoading && filteredAssets.length === 0 && subFolders.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, minHeight: 500 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b" }}>
            Choose from Marketing Assets
          </Typography>
          <Typography variant="caption" sx={{ color: "#64748b" }}>
            {contentType === "Image" ? "Select images" : "Select videos"} from your library
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Search */}
      <Box sx={{ px: 3, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search assets by name…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#94a3b8", fontSize: "1.1rem" }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 2, bgcolor: "#f8fafc" },
          }}
        />
      </Box>

      {/* Breadcrumb */}
      <Box sx={{ px: 3, pb: 0.5 }}>
        <Breadcrumbs separator="›" sx={{ fontSize: "0.8rem" }}>
          <Link
            component="button"
            variant="caption"
            underline="hover"
            onClick={() => setCurrentFolderPath("")}
            sx={{
              color: currentFolderPath ? "#4B5AB6" : "#1e293b",
              fontWeight: currentFolderPath ? 500 : 700,
              cursor: "pointer",
              background: "none",
              border: "none",
            }}
          >
            All Assets
          </Link>
          {breadcrumbParts.map((part, i) => (
            <Link
              key={i}
              component="button"
              variant="caption"
              underline="hover"
              onClick={() => navigateToFolder(i)}
              sx={{
                color: i === breadcrumbParts.length - 1 ? "#1e293b" : "#4B5AB6",
                fontWeight: i === breadcrumbParts.length - 1 ? 700 : 500,
                cursor: "pointer",
                background: "none",
                border: "none",
              }}
            >
              {part}
            </Link>
          ))}
        </Breadcrumbs>
      </Box>

      <DialogContent sx={{ pt: 1 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 280, gap: 2 }}>
            <CircularProgress sx={{ color: "#4B5AB6" }} />
            <Typography variant="body2" sx={{ color: "#64748b" }}>Loading your assets…</Typography>
          </Box>
        ) : isEmpty ? (
          <Box sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}>
            {contentType === "Image"
              ? <ImageIcon sx={{ fontSize: 56, mb: 1, opacity: 0.4 }} />
              : <VideoIcon sx={{ fontSize: 56, mb: 1, opacity: 0.4 }} />
            }
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              No {contentType?.toLowerCase()} assets found
            </Typography>
            <Typography variant="caption">
              Try a different folder, or upload assets first in Marketing Assets
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* Sub-folders */}
            {!searchQuery && subFolders.map((folder) => (
              <Grid item xs={6} sm={4} md={3} key={folder.id}>
                <Paper
                  onClick={() => setCurrentFolderPath(folder.folderPath)}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1.5px solid #e2e8f0",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.75,
                    transition: "border-color 0.15s, background 0.15s",
                    "&:hover": { bgcolor: "#f0f4ff", borderColor: "#4B5AB6" },
                  }}
                >
                  <FolderIcon sx={{ fontSize: 36, color: "#f59e0b" }} />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, textAlign: "center", wordBreak: "break-word", color: "#1e293b" }}
                  >
                    {folder.folderName}
                  </Typography>
                </Paper>
              </Grid>
            ))}

            {/* Asset tiles */}
            {filteredAssets.map((asset) => {
              const isSelected = selectedIds.includes(asset.id);
              const isUsableUrl = /^(https?:|blob:|data:)/i.test(asset.url || "");
              return (
                <Grid item xs={6} sm={4} md={3} key={asset.id}>
                  <Box
                    onClick={() => toggleSelect(asset.id)}
                    sx={{
                      position: "relative",
                      borderRadius: 2,
                      overflow: "hidden",
                      border: isSelected ? "2.5px solid #4B5AB6" : "1.5px solid #e2e8f0",
                      cursor: "pointer",
                      transition: "border-color 0.15s",
                      bgcolor: "#f8fafc",
                      "&:hover": { borderColor: isSelected ? "#4B5AB6" : "#94a3b8" },
                    }}
                  >
                    {/* Thumbnail */}
                    <Box
                      sx={{
                        width: "100%",
                        height: 110,
                        bgcolor: "#e5e7eb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      {asset.type === "image" && isUsableUrl ? (
                        <img
                          src={asset.url}
                          alt={asset.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : asset.type === "video" && isUsableUrl ? (
                        <video
                          src={asset.url}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          muted
                        />
                      ) : asset.type === "image" ? (
                        <ImageIcon sx={{ fontSize: 40, color: "#94a3b8" }} />
                      ) : (
                        <VideoIcon sx={{ fontSize: 40, color: "#94a3b8" }} />
                      )}
                    </Box>

                    {/* Selection checkbox overlay */}
                    <Box sx={{ position: "absolute", top: 5, right: 5 }}>
                      <Checkbox
                        checked={isSelected}
                        size="small"
                        sx={{
                          p: 0.25,
                          color: "rgba(255,255,255,0.9)",
                          bgcolor: isSelected ? "#4B5AB6" : "rgba(255,255,255,0.75)",
                          borderRadius: 1,
                          "&.Mui-checked": { color: "#fff" },
                          "& .MuiSvgIcon-root": { fontSize: "1.1rem" },
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleSelect(asset.id)}
                      />
                    </Box>

                    {/* Name + meta */}
                    <Box sx={{ p: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#1e293b",
                          fontSize: "0.72rem",
                        }}
                        title={asset.name}
                      >
                        {asset.name}
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.3 }}>
                        <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.62rem" }}>
                          {asset.size || ""}
                        </Typography>
                        <Chip
                          label={asset.format || asset.type?.toUpperCase()}
                          size="small"
                          sx={{ height: 16, fontSize: "0.58rem", bgcolor: "#e0e7ff", color: "#4B5AB6", "& .MuiChip-label": { px: 0.75 } }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", justifyContent: "space-between" }}>
        <Typography variant="caption" sx={{ color: "#64748b" }}>
          {selectedIds.length > 0
            ? `${selectedIds.length} asset${selectedIds.length !== 1 ? "s" : ""} selected`
            : "Click an asset to select it"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, borderColor: "#cbd5e1", color: "#64748b" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={selectedIds.length === 0}
            sx={{ borderRadius: 2, bgcolor: "#4B5AB6", fontWeight: 700, "&:hover": { bgcolor: "#3a4891" } }}
          >
            Add {selectedIds.length > 0 ? `${selectedIds.length} ` : ""}Asset{selectedIds.length !== 1 ? "s" : ""}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default MarketingAssetPickerDialog;
